import { useEffect, useState, useRef } from 'react';
import { Navigate, useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { SecurityBondingLayout } from '../../components/security/SecurityBondingLayout';
import { ProgressStepBar } from '../../components/security/ProgressStepBar';
import { FirebaseOneTab } from '../../components/security/FirebaseOneTab';
import { BiometryVerifier } from '../../components/security/BiometryVerifier';
import { GeofenceChecker } from '../../components/security/GeofenceChecker';
import { LivenessRecorder } from '../../components/security/LivenessRecorder';
import { PushPermissionRequest } from '../../components/security/PushPermissionRequest';
import { PhaseEPushVerify } from '../../components/security/PhaseEPushVerify';
import { Bell, Lock, LogOut, OctagonX, PauseCircle, QrCode, ShieldCheck, WifiOff } from 'lucide-react';
import jsQR from 'jsqr';
import { clearClientSession } from '../../auth/sessionCleanup';
import { claimLoginPairingSession } from '../../security/login/loginPairingQrApi';

const API_BASE = (import.meta.env.VITE_API_URL || '/api/v1').replace(/\/$/, '');

type Phase = 'PWA_INSTALL' | 'A' | 'B' | 'C' | 'D' | 'PUSH_PERM' | 'E' | 'DONE' | 'ERROR' | 'LOCKED' | 'BLOCKED' | 'SUSPENDED' | 'CONNECTING' | 'DISCONNECTED';

/** Mapeia currentStep salvo no Redis → fase de resumo.
 *  CONNECTED não está aqui propositalmente — usa initialPhase (PWA_INSTALL no browser, A no PWA). */
const STEP_TO_PHASE: Record<string, Phase> = {
  BIOMETRY_OK: 'C',
  GEO_OK:      'PUSH_PERM',
  LIVENESS_OK: 'PUSH_PERM',
};

// Liveness desativado temporariamente — Prova de Vida sera habilitada futuramente
const LIVENESS_ENABLED = false;

/**
 * SecurityBondingPage — Rota pública /security/bonding?s=SESSION_ID
 * Orquestra o fluxo completo de autenticação forte Mobile↔Desktop:
 * Fase A (Firebase) → B (Biometria) → C (Geofence) → D (Liveness) → E (Push OTP) → APROVADO
 *
 * Ao montar:
 *  1. POST /auth/session/:id/connect  → registra celular, lock (409 se já em uso)
 *  2. Verifica currentStep → resume da etapa correta (page refresh)
 *  3. Após cada fase → POST /auth/session/:id/step → desktop atualiza em tempo real
 */
export default function SecurityBondingPage() {
  const [params] = useSearchParams();
  const routeParams = useParams();
  const navigate = useNavigate();
  // Fallback: após redirect do Google, a query ?s= pode se perder.
  // O FirebaseOneTab salva no sessionStorage antes do redirect.
  const sessionId = params.get('s') || routeParams.sessionId || sessionStorage.getItem('bonding_session_id') || localStorage.getItem('bonding_session_id');
  const intent = params.get('intent') || (routeParams.sessionId ? 'login_pairing' : '');
  const [phase, setPhase] = useState<Phase>('CONNECTING');
  const [errMsg, setErrMsg] = useState('');
  const [otp, setOtp] = useState('');
  const [otpLoading, setOtpLoading] = useState(false);
  const [fcmToken, setFcmToken] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [scanning, setScanning] = useState(false);
  const sseRef = useRef<EventSource | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  /** Qual fase estava ativa antes de desconectar (para continuar mostrando o conteúdo) */
  const [phaseBeforeDisconnect, setPhaseBeforeDisconnect] = useState<Phase | null>(null);
  /** Indica se o celular está de fato vinculado a uma sessão ativa do desktop */
  const [desktopConnected, setDesktopConnected] = useState(false);
  const [redirectCountdown, setRedirectCountdown] = useState(8);
  // Detectar contextos
  const isInsecure = window.location.protocol === 'http:';
  const isStandalone = window.matchMedia('(display-mode: standalone)').matches || (window.navigator as any).standalone === true;
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.userAgent.includes('Mac') && 'ontouchend' in document);

  const handleCancelAndLogout = async () => {
    await clearClientSession();
    window.location.href = '/login';
  };

  // Fase inicial: se já está no PWA ou iOS → pula install, vai direto para biometria (B)
  const forceBrowser = sessionStorage.getItem('ac_force_browser') === 'true' || 
                       new URLSearchParams(window.location.search).get('browser') === '1';
  const initialPhase: Phase = (isStandalone || isIOS || isInsecure || forceBrowser) ? 'B' : 'PWA_INSTALL';

  // stepNumber só para fases válidas
  const stepNumber = { PWA_INSTALL: 1, A: 2, B: 3, C: 3, D: 3, PUSH_PERM: 4, E: 4, DONE: 5 }[phase as string] || 0;

  /** Reporta progresso de etapa ao backend (desktop atualiza em tempo real via SSE) */
  const reportStep = async (step: string) => {
    if (!sessionId) return;
    try {
      await fetch(`${API_BASE}/auth/session/${sessionId}/step`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ step }),
      });
    } catch { /* silencioso — não bloqueia o fluxo mobile */ }
  };

  // Redirecionamento PWA para a rota central /install
  useEffect(() => {
    if (intent === 'login_pairing') return;
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || window.innerWidth < 768;
    const forceBrowserLocal = sessionStorage.getItem('ac_force_browser') === 'true' || 
                              new URLSearchParams(window.location.search).get('browser') === '1';
    const skippedPwaInstall = localStorage.getItem('ac_skip_pwa_install') === 'true';

    if (isMobile && !isStandalone && !isIOS && !isInsecure && !forceBrowserLocal && !skippedPwaInstall && phase === 'PWA_INSTALL') {
      const dest = window.location.pathname + window.location.search;
      navigate(`/install?redirect=${encodeURIComponent(dest)}`, { replace: true });
    }
  }, [phase, intent, isStandalone, isIOS, isInsecure, navigate]);

  // QR do card "Adicionar conta": identifica o usuário do celular e devolve ao desktop.
  //#region AC_BLOCK security.login-pairing-bridge -- Ponte QR para login oficial
  // @ac-purpose Redireciona o QR curto de login para o login oficial, sem renderizar tela negativa no celular.
  // @ac-related react-app/src/pages/auth/LoginPage.tsx#login.qr-auto-login
  // @ac-routes /s/:sessionId; /security/bonding?intent=login_pairing; /login
  useEffect(() => {
    if (intent !== 'login_pairing') return;

    if (!sessionId) {
      setPhase('ERROR');
      setErrMsg('Link de conexão inválido ou sem identificador de sessão.');
      return;
    }

    localStorage.setItem('bonding_session_id', sessionId);
    sessionStorage.setItem('bonding_session_id', sessionId);

    let cancelled = false;

    (async () => {
      try {
        const connectRes = await fetch(`${API_BASE}/auth/session/${sessionId}/connect`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
        });

        if (connectRes.status === 409) {
          setPhase('LOCKED');
          setErrMsg('Este QR Code / Link já está sendo utilizado em outro dispositivo.');
          return;
        }

        if (!connectRes.ok) {
          setPhase('ERROR');
          setErrMsg('Sessão expirada ou não encontrada no servidor (404). O tempo limite de espera foi atingido ou o QR Code expirou.');
          return;
        }

        await claimLoginPairingSession(sessionId);

        if (cancelled) return;

        setDesktopConnected(true);
        setPhase('DONE');
        localStorage.removeItem('bonding_session_id');
        sessionStorage.removeItem('bonding_session_id');
      } catch (err: any) {
        if (cancelled) return;
        setPhase('ERROR');
        setErrMsg('Falha ao conectar com o servidor. Verifique sua conexão e tente novamente.');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [intent, sessionId, navigate]);
  //#endregion AC_BLOCK security.login-pairing-bridge

  // ── Ao montar: connect (lock) + resume ────────────────────────────────────
  useEffect(() => {
    if (intent === 'login_pairing') return;
    if (!sessionId) {
      const hasToken = !!localStorage.getItem('ac_token');
      if (hasToken) {
        window.location.href = '/mobile';
        return;
      }
      setPhase('ERROR');
      setErrMsg('Link inválido. Escaneie o QR Code novamente.');
      return;
    }

    // Persistir session ID para o PWA poder ler (localStorage é compartilhado)
    localStorage.setItem('bonding_session_id', sessionId);
    sessionStorage.setItem('bonding_session_id', sessionId);

    (async () => {
      try {
        // 1. Registrar celular na sessão (lock) e auto-identificar
        const connectRes = await fetch(`${API_BASE}/auth/session/${sessionId}/connect`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
        });

        if (connectRes.status === 409) {
          setPhase('LOCKED');
          return;
        }
        if (!connectRes.ok && connectRes.status !== 200) {
          const hasToken = !!localStorage.getItem('ac_token');
          if (hasToken) {
            console.log('[SecurityBonding] Session invalid or expired, but token exists. Redirecting to /mobile...');
            localStorage.removeItem('bonding_session_id');
            sessionStorage.removeItem('bonding_session_id');
            window.location.href = '/mobile';
            return;
          }
          setPhase('ERROR');
          setErrMsg('Sessão inválida ou expirada. Escaneie o QR novamente.');
          return;
        }

        const connectData = await connectRes.json();
        // Se retornar tokens de bonding, salvá-los no localStorage
        if (connectData.access_token) {
          localStorage.setItem('ac_token', connectData.access_token);
        }
        if (connectData.refresh_token) {
          localStorage.setItem('ac_refresh_token', connectData.refresh_token);
        }
        if (connectData.user) {
          localStorage.setItem('ac_user', JSON.stringify(connectData.user));
          if (connectData.user.perfil) localStorage.setItem('ac_perfil', connectData.user.perfil);
          if (connectData.user.escolaId) localStorage.setItem('ac_escolaId', connectData.user.escolaId);
          if (connectData.user.nomeEscola) localStorage.setItem('ac_nomeEscola', connectData.user.nomeEscola);
          if (connectData.user.cargo) localStorage.setItem('ac_cargo', connectData.user.cargo);
        }

        // 2. Verificar status real da sessão (o desktop pode já ter gerado outro QR)
        const statusRes = await fetch(`${API_BASE}/auth/link-status/${sessionId}`);
        if (statusRes.ok) {
          const { currentStep, status } = await statusRes.json();

          if (status === 'PENDING') {
            setDesktopConnected(false);
            setPhase(initialPhase);
          } else {
            setDesktopConnected(true);
            const resumePhase = currentStep ? STEP_TO_PHASE[currentStep] : null;
            // Se a sessão acabou de se conectar/identificar, segue a fase inicial (PWA_INSTALL se no browser, B se no PWA)
            if (currentStep === 'CONNECTED' || currentStep === 'IDENTIFIED') {
              setPhase(initialPhase);
            } else {
              setPhase(resumePhase ?? initialPhase);
            }
          }
        } else {
          setDesktopConnected(false);
          setPhase(initialPhase);
        }
      } catch {
        setPhase(initialPhase); // Fallback — inicia do começo
      }
    })();

    // ── Heartbeat ping a cada 30s — mantém sessão viva e reseta timeout do desktop ──
    const PING_INTERVAL = 30_000;
    const pingUrl = `${API_BASE}/auth/session/${sessionId}/ping`;
    const pingTimer = setInterval(() => {
      fetch(pingUrl, { method: 'POST', headers: { 'Content-Type': 'application/json' } })
        .catch(() => { /* silencioso — não bloqueia o fluxo */ });
    }, PING_INTERVAL);

    // ── Notificar backend quando celular FECHAR a página (não refresh) ─────
    const disconnectUrl = `${API_BASE}/auth/session/${sessionId}/disconnect`;
    const onPageHide = (e: PageTransitionEvent) => {
      if (!e.persisted) {
        navigator.sendBeacon(disconnectUrl, JSON.stringify({}));
      }
    };
    window.addEventListener('pagehide', onPageHide);

    return () => {
      clearInterval(pingTimer);
      window.removeEventListener('pagehide', onPageHide);
      sseRef.current?.close();
    };
  }, [sessionId, intent]);

  // ── Escutar SSE do backend (desktop pode desconectar) ─────────────────
  useEffect(() => {
    if (!sessionId) return;
    const sse = new EventSource(`${API_BASE}/auth/session-sse/${sessionId}`, { withCredentials: true });
    sseRef.current = sse;

    sse.addEventListener('desktop_disconnected', () => {
      setDesktopConnected(false);
      setPhase(prev => {
        setPhaseBeforeDisconnect(prev);
        return 'DISCONNECTED';
      });
    });

    sse.onerror = () => {
      // SSE pode falhar normalmente, ignora
    };

    return () => sse.close();
  }, [sessionId]);

  // Redirecionamento automático quando a fase for DONE para levar o usuário ao app logado
  useEffect(() => {
    if (phase !== 'DONE') return;
    const interval = setInterval(() => {
      setRedirectCountdown(prev => {
        if (prev <= 1) {
          clearInterval(interval);
          window.location.href = '/mobile';
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [phase]);

  // (countdown removido — DISCONNECTED é apenas mudança de status no card)

  /** Abre a câmera nativa do celular via input file (funciona Android + iOS, mesmo HTTP) */
  const handleQrScan = () => {
    setErrMsg('');
    fileInputRef.current?.click();
  };

  /** Decodifica o QR Code da foto capturada usando jsQR */
  const handleFileCapture = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setScanning(true);
    setErrMsg('');

    try {
      // Cria um canvas para extrair os pixels da imagem
      const img = new Image();
      const url = URL.createObjectURL(file);

      await new Promise<void>((resolve, reject) => {
        img.onload = () => resolve();
        img.onerror = () => reject(new Error('Erro ao carregar imagem'));
        img.src = url;
      });

      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d')!;
      ctx.drawImage(img, 0, 0);
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      URL.revokeObjectURL(url);

      // Decodifica com jsQR
      const code = jsQR(imageData.data, imageData.width, imageData.height);

      if (code && code.data) {
        console.log('[QR Scanner] Decodificado:', code.data);
        // Verifica se é uma URL válida do sistema
        if (code.data.includes('/security/bonding')) {
          window.location.href = code.data;
          return;
        } else {
          setErrMsg('QR Code inválido. Escaneie o QR Code que aparece no computador.');
        }
      } else {
        setErrMsg('Não foi possível ler o QR Code. Tente novamente com a câmera mais próxima.');
      }
    } catch (err) {
      console.error('[QR Scanner] Erro:', err);
      setErrMsg('Erro ao processar a imagem. Tente novamente.');
    } finally {
      setScanning(false);
      // Limpa o input para permitir reescanear o mesmo arquivo
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleError = (msg: string) => { setErrMsg(msg); setPhase('ERROR'); };

  // Fase E — enviar push OTP usando o token FCM real obtido na fase PUSH_PERM
  const registrarPush = async (tokenOverride?: string) => {
    if (!sessionId) return;
    const token = tokenOverride || fcmToken || localStorage.getItem('fcmToken');
    if (!token) {
      setErrMsg('Token FCM não disponível. Volte e ative as notificações.');
      return;
    }
    setOtpLoading(true);
    try {
      const res = await fetch(`${API_BASE}/security/session/${sessionId}/push-register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ fcmToken: token }),
      });
      if (!res.ok) throw new Error('Erro ao registrar push');
      setOtpSent(true);
    } catch (e: any) {
      setErrMsg(e.message);
    } finally {
      setOtpLoading(false);
    }
  };

  const confirmarOtp = async () => {
    if (!sessionId || otp.length < 4) return;
    setOtpLoading(true);
    try {
      const res = await fetch(`${API_BASE}/security/session/${sessionId}/push-confirm`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ otp }),
      });
      if (!res.ok) throw new Error('Código inválido');
      await reportStep('DONE');
      sessionStorage.removeItem('bonding_session_id');
      localStorage.removeItem('bonding_session_id');
      setPhase('DONE');
      window.location.href = '/security/success';
    } catch {
      setErrMsg('Código incorreto. Verifique e tente novamente.');
    } finally {
      setOtpLoading(false);
    }
  };

  // ══════════════════════════════════════════════════════════════════════════
  // TELAS SEM HEADER / SEM BARRA DE PROGRESSO (fullscreen informativo)
  // ══════════════════════════════════════════════════════════════════════════

  // ── Tela CONNECTING ───────────────────────────────────────────────────────
  if (phase === 'CONNECTING') {
    return (
      <SecurityBondingLayout>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 16, padding: '3rem 1rem', textAlign: 'center' }}>
          <div style={{ width: 48, height: 48, border: '3px solid rgba(196,181,253,.2)', borderTopColor: '#a855f7', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
          <p style={{ color: '#ddd6fe', fontSize: '0.9rem' }}>Conectando sessão segura...</p>
        </div>
      </SecurityBondingLayout>
    );
  }

  if (phase === 'ERROR') {
    return <Navigate to="/login" replace />;
  }

  // ── Tela LOCKED / BLOCKED / SUSPENDED ─────────────────────────────────────
  // Fullscreen sem header, sem barra de progresso — estados de politica.
  if (phase === 'LOCKED' || phase === 'BLOCKED' || phase === 'SUSPENDED') {
    const configs: Record<string, { icon: string; title: string; defaultMsg: string; btnText: string; accent: string; accentBg: string }> = {
      LOCKED:    { icon: '🔒', title: 'Link já utilizado',             defaultMsg: 'Este QR Code já está em uso em outro dispositivo.', btnText: 'Entendi',     accent: '#fbbf24', accentBg: 'rgba(251,191,36,.08)' },
      BLOCKED:   { icon: '⛔', title: 'Acesso bloqueado',              defaultMsg: 'Seu acesso foi bloqueado pelo administrador da escola. Entre em contato com a coordenação.', btnText: 'Entendi', accent: '#ef4444', accentBg: 'rgba(239,68,68,.08)' },
      SUSPENDED: { icon: '⏸️', title: 'Acesso suspenso temporariamente', defaultMsg: 'Seu acesso está temporariamente suspenso. Aguarde ou entre em contato com a escola.', btnText: 'Entendi', accent: '#f59e0b', accentBg: 'rgba(245,158,11,.08)' },
    };
    const cfg = configs[phase] || configs.LOCKED;
    const statusIcons: Record<string, typeof Lock> = {
      LOCKED: Lock,
      BLOCKED: OctagonX,
      SUSPENDED: PauseCircle,
    };
    const StatusIcon = statusIcons[phase] || Lock;
    const handleStatusAction = () => {
      setErrMsg('');
      setPhase(initialPhase);
    };

    return (
      <div style={{
        minHeight: '100dvh',
        background: 'radial-gradient(circle at top, rgba(168,85,247,0.28) 0%, transparent 34%), linear-gradient(160deg, #13051f 0%, #251050 46%, #090313 100%)',
        color: '#f8fafc',
        fontFamily: 'system-ui, -apple-system, sans-serif',
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        padding: '2rem 1.5rem', textAlign: 'center',
      }}>
        <div style={{
          width: 'min(420px, 100%)',
          borderRadius: 28,
          padding: '34px 24px 28px',
          background: 'linear-gradient(180deg, rgba(46,16,101,0.90) 0%, rgba(30,12,64,0.92) 100%)',
          border: '1px solid rgba(221, 214, 254, 0.16)',
          boxShadow: '0 26px 72px rgba(17, 7, 45, 0.66), inset 0 1px 0 rgba(255,255,255,0.14)',
          backdropFilter: 'blur(22px)',
          WebkitBackdropFilter: 'blur(22px)',
          boxSizing: 'border-box',
        }}>
        {/* Ícone com ring animado */}
        <div style={{ position: 'relative', marginBottom: 28 }}>
          <div style={{
            width: 88, height: 88, borderRadius: '50%',
            background: cfg.accentBg, border: `2px solid ${cfg.accent}30`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: cfg.accent,
            animation: 'bonding-pop 0.5s cubic-bezier(.175,.885,.32,1.275)',
          }}>
            <StatusIcon size={42} strokeWidth={2.4} />
          </div>
          <div style={{
            position: 'absolute', inset: -8, borderRadius: '50%',
            border: `1px solid ${cfg.accent}15`,
            animation: 'bonding-ring 2s ease-out infinite',
          }} />
        </div>

        {/* Título */}
        <h1 style={{
          fontSize: '1.25rem', fontWeight: 800, color: cfg.accent,
          margin: '0 0 10px', letterSpacing: '-0.01em',
        }}>
          {cfg.title}
        </h1>

        {/* Mensagem */}
        <p style={{
          fontSize: '0.9rem', color: '#ddd6fe', maxWidth: 320,
          margin: '0 0 32px', lineHeight: 1.7,
        }}>
          {errMsg || cfg.defaultMsg}
        </p>

        {/* Botão de ação */}
        <button
          onClick={handleStatusAction}
          style={{
            padding: '15px 42px', borderRadius: 16,
            background: 'linear-gradient(135deg, #a855f7 0%, #7c3aed 52%, #581c87 100%)',
            color: '#ffffff', fontSize: '0.95rem', fontWeight: 800,
            cursor: 'pointer', letterSpacing: '0.02em',
            backdropFilter: 'blur(8px)',
            border: '1px solid rgba(255,255,255,0.20)',
            boxShadow: '0 5px 0 #3b0764, 0 16px 28px rgba(88,28,135,0.42)',
            transition: 'all 0.2s ease',
          }}
        >
          {cfg.btnText}
        </button>

        {/* Informação adicional */}
        <p style={{
          fontSize: '0.78rem', color: '#c4b5fd', marginTop: 24,
          maxWidth: 280, lineHeight: 1.5,
        }}>
          Se o problema persistir, entre em contato com o administrador da sua escola.
        </p>

        {/* Desconectar */}
        <button
          onClick={handleCancelAndLogout}
          style={{
            marginTop: 16, background: 'none', border: 'none', cursor: 'pointer',
            color: '#fca5a5', fontSize: '.78rem', fontWeight: 700, padding: '6px 12px',
            transition: 'color .2s',
            textDecoration: 'underline',
            zIndex: 10,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 7,
          }}
          onMouseEnter={e => (e.currentTarget.style.color = '#fca5a5')}
          onMouseLeave={e => (e.currentTarget.style.color = '#fca5a5')}
        >
          <LogOut size={15} />
          Sair / Cancelar Pareamento
        </button>
        </div>

        <style>{`
          @keyframes bonding-pop { 0% { transform: scale(0.5); opacity: 0; } 100% { transform: scale(1); opacity: 1; } }
          @keyframes bonding-ring { 0% { transform: scale(1); opacity: 0.4; } 100% { transform: scale(1.4); opacity: 0; } }
          @keyframes bonding-pulse { 0%,100% { opacity:.4; transform:scale(.8); } 50% { opacity:1; transform:scale(1.2); } }
          @keyframes spin { to { transform: rotate(360deg); } }
        `}</style>
      </div>
    );
  }

  // DISCONNECTED agora é tratado inline no fluxo normal (card muda)

  // ══════════════════════════════════════════════════════════════════════════
  // FLUXO NORMAL — com header + barra de progresso
  // ══════════════════════════════════════════════════════════════════════════

  if (!sessionId) {
    return (
      <SecurityBondingLayout>
        <div style={{ textAlign: 'center', paddingTop: '2rem' }}>
          <p style={{ color: '#ef4444' }}>Link inválido. Escaneie o QR Code novamente.</p>
        </div>
      </SecurityBondingLayout>
    );
  }

  const isDisconnected = phase === 'DISCONNECTED' || !desktopConnected;
  const activePhase = phase === 'DISCONNECTED' ? phaseBeforeDisconnect : phase;

  return (
    <SecurityBondingLayout>
      {/* Input file oculto — abre câmera nativa no Android e iOS (funciona mesmo em HTTP) */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handleFileCapture}
        style={{ display: 'none' }}
      />

      {/* Mensagem de reconexão quando desconectado */}
      {isDisconnected && (
        <div style={{
          margin: '0 0 16px', padding: '14px 16px', borderRadius: 14,
          background: 'rgba(239,68,68,.08)', border: '1px solid rgba(239,68,68,.2)',
          display: 'flex', alignItems: 'center', gap: 12,
        }}>
          <WifiOff size={20} style={{ color: '#ef4444', flexShrink: 0 }} />
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: '.92rem', fontWeight: 700, color: '#fca5a5' }}>Desconectado</div>
            <div style={{ fontSize: '.78rem', color: '#94a3b8', marginTop: 2 }}>Escaneie o QR Code novamente para reconectar</div>
          </div>
          <button
            onClick={handleQrScan}
            disabled={scanning}
            style={{
              padding: '8px 14px', borderRadius: 10, border: 'none',
              background: '#ef4444', color: '#fff', fontSize: '.82rem', fontWeight: 600,
              cursor: scanning ? 'wait' : 'pointer',
            }}
          >
            <QrCode size={16} />
          </button>
        </div>
      )}

      {/* Barra de progresso — oculta quando desconectado */}
      {stepNumber > 0 && !isDisconnected && <ProgressStepBar currentStep={stepNumber} />}

      {/* ═══ FASE 1: INSTALAR APP (só no browser Chrome Android) ═══ */}
      {activePhase === 'PWA_INSTALL' && (
        <div style={{ textAlign: 'center', padding: '2rem' }}>
          <div style={{
            width: 40, height: 40, border: '3px solid rgba(168, 85, 247, 0.1)',
            borderTopColor: '#c084fc', borderRadius: '50%',
            margin: '0 auto 16px', animation: 'spin .8s linear infinite',
          }} />
          <p style={{ color: '#94a3b8', fontSize: '0.9rem' }}>Redirecionando para a instalação do PWA...</p>
        </div>
      )}

      {activePhase === 'A' && (
        <FirebaseOneTab
          sessionId={sessionId}
          apiBase={API_BASE}
          onSuccess={async () => {
            if (isInsecure) {
              // HTTP: auto-completar bonding via endpoint dev-only
              try {
                await fetch(`${API_BASE}/security/session/${sessionId}/dev-complete`, { method: 'POST' });
              } catch { /* silencioso */ }
              reportStep('DONE');
              sessionStorage.removeItem('bonding_session_id');
              localStorage.removeItem('bonding_session_id');
              setPhase('DONE');
            } else {
              setPhase('B');
            }
          }}
          onError={handleError}
        />
      )}

      {activePhase === 'B' && (
        <BiometryVerifier
          sessionId={sessionId}
          apiBase={API_BASE}
          onSuccess={() => { reportStep('BIOMETRY_OK'); setPhase('C'); }}
          onError={handleError}
        />
      )}

      {activePhase === 'C' && (
        <GeofenceChecker
          sessionId={sessionId}
          apiBase={API_BASE}
          onSuccess={() => { reportStep('GEO_OK'); setPhase('PUSH_PERM'); }}
          onSkip={() => {
            reportStep('GEO_OK');
            // Liveness desativado: sempre pula para notificacoes
            if (LIVENESS_ENABLED) {
              setPhase('D');
            } else {
              setPhase('PUSH_PERM');
            }
          }}
          onError={handleError}
        />
      )}

      {/* Prova de Vida — desativada temporariamente */}
      {LIVENESS_ENABLED && activePhase === 'D' && (
        <LivenessRecorder
          sessionId={sessionId}
          apiBase={API_BASE}
          onSuccess={() => { reportStep('LIVENESS_OK'); setPhase('PUSH_PERM'); }}
          onError={handleError}
        />
      )}

      {activePhase === 'PUSH_PERM' && (
        <PushPermissionRequest
          onSuccess={(token) => {
            setFcmToken(token);
            localStorage.setItem('fcmToken', token);
            if (token === 'mock-fcm-token-unsupported') {
              // Bypass total da Fase E (OTP) para navegadores sem notificacao
              (async () => {
                try {
                  await fetch(`${API_BASE}/security/session/${sessionId}/push-confirm`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ otp: '0000' }),
                  });
                  await reportStep('DONE');
                  sessionStorage.removeItem('bonding_session_id');
                  localStorage.removeItem('bonding_session_id');
                  setPhase('DONE');
                  window.location.href = '/security/success';
                } catch (err: any) {
                  setErrMsg(err.message || 'Erro ao confirmar sessão');
                  setPhase('E');
                }
              })();
            } else {
              setPhase('E');
              // Auto-enviar push OTP ao obter token
              setTimeout(() => registrarPush(token), 300);
            }
          }}
        />
      )}

      {activePhase === 'E' && (
        <PhaseEPushVerify
          sessionId={sessionId!}
          fcmToken={fcmToken}
          registrarPush={registrarPush}
          onDone={async () => {
            await reportStep('DONE');
            // Limpar bonding session — convite nao pode mais ser usado
            sessionStorage.removeItem('bonding_session_id');
            localStorage.removeItem('bonding_session_id');
            setPhase('DONE');
            // NAO redireciona — mostra tela de conclusao com instrucoes
          }}
          errMsg={errMsg}
        />
      )}

      {activePhase === 'DONE' && (
        <div className="glass-card animate-fade-in-up" style={{ 
          textAlign: 'center', padding: '3rem 1.5rem', borderRadius: 24,
          position: 'relative', overflow: 'hidden'
        }}>
          {/* Glow background behind the card */}
          <div style={{
            position: 'absolute', top: -50, left: '50%', transform: 'translateX(-50%)',
            width: 200, height: 200, background: 'rgba(16, 185, 129, 0.15)',
            filter: 'blur(50px)', zIndex: 0
          }} />

          <div style={{ position: 'relative', zIndex: 1 }}>
            <div style={{
              width: 96, height: 96, borderRadius: '50%',
              background: 'linear-gradient(135deg, rgba(16,185,129,.2), rgba(5,150,105,.1))', 
              border: '2px solid rgba(16,185,129,0.5)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto 1.5rem',
              boxShadow: '0 0 30px rgba(16,185,129,0.2), inset 0 0 15px rgba(16,185,129,0.1)',
              animation: 'bonding-pulse 2s ease-in-out infinite',
            }}>
              <ShieldCheck size={48} strokeWidth={1.5} style={{ color: '#10b981' }} />
            </div>
            
            <h2 style={{ fontSize: '1.6rem', fontWeight: 800, color: '#f8fafc', marginBottom: '0.75rem', letterSpacing: '-0.02em' }}>
              Processo <span style={{ color: '#34d399' }}>Concluido!</span>
            </h2>
            <p style={{ fontSize: '0.95rem', color: '#94a3b8', lineHeight: 1.7, maxWidth: 300, margin: '0 auto 1.25rem' }}>
              Seu dispositivo movel foi autenticado com sucesso e o pareamento seguro esta ativo.
            </p>

            {/* Instrucao para desktop */}
            <div style={{
              padding: '16px 20px', borderRadius: 16, marginBottom: '1rem',
              background: 'rgba(124,58,237,0.12)',
              border: '1px solid rgba(196,181,253,0.24)',
            }}>
              <p style={{ fontSize: '0.88rem', color: '#ddd6fe', lineHeight: 1.6, margin: 0, fontWeight: 600 }}>
                Agora volte ao computador e cadastre sua chave de autenticacao la tambem.
              </p>
            </div>

            <button
              onClick={() => { window.location.href = '/mobile'; }}
              style={{
                width: '100%', padding: '0.85rem 1rem', borderRadius: 12, border: 'none',
                background: 'linear-gradient(135deg, #10b981, #059669)',
                color: '#ffffff',
                boxShadow: '0 4px 15px rgba(16, 185, 129, 0.25)',
                fontSize: '0.95rem', fontWeight: 700,
                cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
                transition: 'all 0.2s',
                marginTop: '1.25rem',
              }}
              onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 6px 20px rgba(16, 185, 129, 0.3)'; }}
              onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 15px rgba(16, 185, 129, 0.25)'; }}
            >
              Entrar no Aplicativo
            </button>

            <p style={{ fontSize: '0.78rem', color: '#64748b', marginTop: '0.85rem' }}>
              Redirecionando para o aplicativo em {redirectCountdown}s...
            </p>

            <p style={{ fontSize: '0.72rem', color: '#475569', margin: '0.75rem 0 0' }}>
              Este convite nao pode mais ser utilizado.
            </p>
          </div>
        </div>
      )}
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes bonding-pulse { 0%,100% { opacity:.4; transform:scale(.8); } 50% { opacity:1; transform:scale(1.2); } }
      `}</style>
    </SecurityBondingLayout>
  );
}
